import * as Api from '@ViewModels';

/**
 * Converts AI-provided ContactAudienceInfo into contact filter criteria.
 * Handles both tag-based filters (dynamic audience) and explicit contact IDs.
 *
 * Filter Logic:
 * - Multiple tag filters → OR logic (union): "VC" OR "VC 2024" OR "VC: Stage 2"
 * - Multiple criteria in single filter → AND logic (intersection): "VIP" AND "Q4"
 * - Single filter with single criterion → direct filter
 *
 * All tag filters are wrapped with exclusion criteria (no email addresses, excluded tags)
 * to match the standard bulk send filtering behavior.
 *
 * @param audienceInfo - Audience information from AI Assistant
 * @returns Contact filter request with exclusions applied, ready for email composer
 */
export function buildContactFilterFromAudienceInfo(
	audienceInfo: Api.ContactAudienceInfo
): Api.IEmailMessageContactsFilterRequest | null {
	if (!audienceInfo.tagFilters?.length) {
		return null;
	}

	// Build exclusion criteria (used for all filter scenarios)
	const exclusionCriteria: Api.IContactFilterCriteria[] = [
		{
			op: Api.FilterOperator.Not,
			property: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
		},
		...Array.from(Api.DefaultBulkSendExcludedTags).map(
			x =>
				({
					op: Api.FilterOperator.Not,
					property: Api.ContactFilterCriteriaProperty.Tag,
					value: x,
				}) as Api.IContactFilterCriteria
		),
	];

	// SCENARIO 1: Multiple filters → OR logic (UNION)
	if (audienceInfo.tagFilters.length > 1) {
		const orCriteria: Api.IContactFilterCriteria[] = audienceInfo.tagFilters.map(filter => {
			const filterCriteria = filter.criteria
				.filter(c => c.property === 'Tag')
				.map(c => ({
					property: Api.ContactFilterCriteriaProperty.Tag,
					value: c.value,
				}));

			if (filterCriteria.length > 1) {
				return {
					op: Api.CONTACT_FILTER_OPERATOR.And,
					criteria: filterCriteria,
				};
			}
			return filterCriteria[0];
		});

		// Wrap OR filters with exclusions in AND
		return {
			contactFilterRequest: {
				op: Api.FilterOperator.And,
				criteria: [
					{
						op: Api.CONTACT_FILTER_OPERATOR.Or,
						criteria: orCriteria,
					},
					...exclusionCriteria,
				],
			},
		};
	}

	// SCENARIO 2 & 3: Single filter
	const singleFilter = audienceInfo.tagFilters[0];
	const criteria = singleFilter.criteria
		.filter(c => c.property === 'Tag')
		.map(c => ({
			property: Api.ContactFilterCriteriaProperty.Tag,
			value: c.value,
		}));

	if (criteria.length > 1) {
		// SCENARIO 2: Multiple criteria in single filter → AND logic
		return {
			contactFilterRequest: {
				op: Api.FilterOperator.And,
				criteria: [...criteria, ...exclusionCriteria],
			},
		};
	}

	// SCENARIO 3: Single filter with single criterion
	return {
		contactFilterRequest: {
			op: Api.FilterOperator.And,
			criteria: [criteria[0], ...exclusionCriteria],
		},
	};
}
